import React, { useEffect, useRef, useState } from 'react';
import SEO from '../components/SEO';
import TrailButton from '../components/TrailButton';

/* ─── Data ─── */
const capabilities = [
  '🤖 Voice Agents', '💬 AI Chatbots', '📞 Lead Follow-Up', '📅 Appointment Booking',
  '⚡ Workflow Automation', '📊 CRM Integration', '📧 Email Sequences', '🎯 Lead Qualification',
  '🔄 Process Automation', '📱 SMS Follow-Up', '🧠 Custom AI Agents', '🔗 API Integrations',
  '📋 Intake Automation', '🎙️ AI Receptionists', '💰 Revenue Tracking', '🚀 Scale Operations',
];

const trustLogos = [
  'https://cdn.brandfetch.io/id8aZ2xxxo/w/1234/h/299/theme/dark/logo.png?c=1bxid2-Pt-fvQtMo99_KJ',
  'https://cdn.brandfetch.io/id2Syei8yU/w/820/h/190/theme/dark/logo.png?c=1bxid2-Pt-fvQtMo99_KJ',
  'https://cdn.brandfetch.io/idAoYnssvN/w/300/h/85/theme/dark/logo.png?c=1bxid2-Pt-fvQtMo99_KJ',
  'https://cdn.brandfetch.io/idHb2bIejT/w/300/h/134/theme/dark/logo.png?c=1bxid2-Pt-fvQtMo99_KJ',
  'https://cdn.brandfetch.io/id5EQEHkGR/w/2411/h/535/theme/light/logo.png?c=1bxid2-Pt-fvQtMo99_KJ',
  'https://cdn.brandfetch.io/id3XRNGYg9/w/820/h/636/theme/dark/logo.png?c=1bxid2-Pt-fvQtMo99_KJ',
  'https://cdn.brandfetch.io/id_KsO4R0w/w/355/h/68/theme/dark/logo.png?c=1bxid2-Pt-fvQtMo99_KJ',
  'https://cdn.brandfetch.io/idLBGUHyLa/w/176/h/80/theme/dark/logo.png?c=1bxid2-Pt-fvQtMo99_KJ',
  'https://cdn.brandfetch.io/idtNCLdizt/w/300/h/126/theme/light/logo.png?c=1bxid2-Pt-fvQtMo99_KJ',
  'https://cdn.brandfetch.io/idadYcYk66/w/575/h/86/theme/light/logo.png?c=1bxid2-Pt-fvQtMo99_KJ',
];

/* ─── Reusable components ─── */
const TrustBar = () => (
  <div className="gl-trust" data-testid="gl-trust-bar">
    <span className="gl-trust__inner">
      <strong>4.7 </strong>
      <span className="gl-trust__stars">★★★★★</span>
      <span className="gl-trust__sep"> | </span>
      <strong>Trusted by 300+ Businesses</strong>
    </span>
  </div>
);

const CTAButton = ({ onClick }) => (
  <div className="skye-cta-wrap" data-testid="gl-cta-wrap">
    <TrailButton>
      <button className="skye-cta" onClick={onClick} data-testid="gl-cta-button">
        Book My Growth Planning Call <span className="arw">&rarr;</span>
      </button>
    </TrailButton>
    <span className="skye-cta-note skye-paymodel">30-minute strategy call — zero commitment.</span>
  </div>
);

const LogoBar = () => (
  <div className="gl-logos" data-testid="gl-logos">
    <div className="gl-logos__inner">
      {[...trustLogos, ...trustLogos].map((src, i) => (
        <img key={`logo-${i}`} src={src} alt={`Partner ${(i % trustLogos.length) + 1}`} className="gl-logos__img" />
      ))}
    </div>
  </div>
);

/* ─── Main Component ─── */
const GetLeads = () => {
  const iframeRef = useRef(null);
  const iClosedRef = useRef(null);
  const [showStickyCta, setShowStickyCta] = useState(false);
  const [showExitIntent, setShowExitIntent] = useState(false);
  const exitShownRef = useRef(false);

  /* Show sticky CTA after scrolling past the hero CTA */
  useEffect(() => {
    window.scrollTo(0, 0);
    const handleScroll = () => {
      setShowStickyCta(window.scrollY > 600);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  /* Exit Intent Detection — fires once per session */
  useEffect(() => {
    if (sessionStorage.getItem('exitIntentShown')) return;

    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !exitShownRef.current) {
        exitShownRef.current = true;
        sessionStorage.setItem('exitIntentShown', '1');
        setShowExitIntent(true);
      }
    };

    document.addEventListener('mouseout', handleMouseLeave);
    return () => document.removeEventListener('mouseout', handleMouseLeave);
  }, []);

  /* Load testimonial.to iFrameResize script */
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://testimonial.to/js/iframeResizer.min.js';
    script.async = true;
    script.onload = () => {
      if (window.iFrameResize && iframeRef.current) {
        window.iFrameResize({ log: false, checkOrigin: false }, '#testimonialto-skye-ai');
      }
    };
    document.body.appendChild(script);
    return () => { document.body.removeChild(script); };
  }, []);

  /* Load iClosed widget via ref (avoids innerHTML XSS risk) */
  useEffect(() => {
    const container = iClosedRef.current;
    if (!container) return;

    // Clear any previous children (handles React Strict Mode double-invoke)
    while (container.firstChild) container.removeChild(container.firstChild);

    const iframe = document.createElement('iframe');
    iframe.src = 'https://app.iclosed.io/e/skyeai/ai-growth-planning-call-expedited-1';
    iframe.style.cssText = 'width:100%;border:none;display:block;overflow:hidden;';
    iframe.scrolling = 'no';
    iframe.title = 'Book Growth Planning Call';
    iframe.setAttribute('allow', 'clipboard-write');
    // Responsive height: mobile stacked layout needs more room
    const isMobile = window.innerWidth < 768;
    iframe.style.height = isMobile ? '850px' : '500px';

    // Listen for iClosed postMessage to auto-resize
    const handleMessage = (e) => {
      if (e.data && typeof e.data === 'string') {
        try {
          const msg = JSON.parse(e.data);
          if (msg.height) iframe.style.height = msg.height + 'px';
        } catch {}
      }
      if (e.data && typeof e.data === 'object' && e.data.height) {
        iframe.style.height = e.data.height + 'px';
      }
    };
    window.addEventListener('message', handleMessage);
    container.appendChild(iframe);

    return () => {
      window.removeEventListener('message', handleMessage);
      while (container.firstChild) container.removeChild(container.firstChild);
    };
  }, []);

  const scrollToBooking = () => {
    const el = document.getElementById('skyeBook');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="gl-page" data-testid="get-leads-page">
      <SEO
        title="Book Your Growth Planning Call | Skye AI"
        description="Custom AI voice agents, chatbots, and automation systems built and managed for your business. Book a free Growth Planning Call to see how AI can scale your operations."
        keywords="custom AI buildouts, AI voice agents, business automation, AI chatbots, workflow automation, appointment booking AI"
        path="/getleads"
      />

      {/* ── Sticky Logo ── */}
      <header className="gl-topbar" data-testid="gl-topbar">
        <img
          src="https://perspective.imgix.net/68780c456625fd003b27e7c2.png?auto=format,compress&dpr=2&fit=crop&h=32&q=80"
          alt="Skye AI" className="gl-topbar__logo"
        />
      </header>

      {/* ── Qualification Pill + Capability Ticker ── */}
      <div className="skye-qual" data-testid="gl-qual-pill">
        <div className="skye-qual-pill">
          <span className="skye-qual-pill-in">
            <span className="skye-qual-dot"><i /></span>
            <b>For Business Owners Ready to Scale with AI</b>
          </span>
        </div>
        <div className="skye-qual-ticker">
          <div className="skye-qual-track">
            {[...capabilities, ...capabilities].map((t, i) => (
              <i key={`t-${i}`} style={{ '--i': i }}>{t}</i>
            ))}
          </div>
        </div>
      </div>

      {/* ── Hero ── */}
      <div className="skye-hero" data-testid="gl-hero">
        <div className="skye-intro">
          <span className="skye-intro-line left" />
          <span className="skye-intro-text">Get</span>
          <span className="skye-intro-line right" />
        </div>
        <h1 className="skye-hero-title" data-testid="gl-hero-title">
          Custom AI Systems That Handle Communication, Booking &amp; <span className="engine">Automation.</span>
        </h1>
        <p className="skye-hero-sub">
          We build AI voice agents, chatbots, and workflow automations tailored to your business.{' '}
          Done for you. Managed by our team. Running 24/7.
        </p>

        <div className="skye-hero-stats" data-testid="gl-hero-stats">
          <div className="skye-hero-stat">
            <b>300<span>+</span></b>
            <small>Businesses<br />served</small>
          </div>
          <div className="skye-hero-stat">
            <b>24<span>/7</span></b>
            <small>AI systems<br />running</small>
          </div>
          <div className="skye-hero-stat">
            <b>30<span>-Day</span></b>
            <small>Setup to<br />go live</small>
          </div>
        </div>
      </div>

      {/* ── CTA ── */}
      <CTAButton onClick={scrollToBooking} />

      {/* ── Trust ── */}
      <TrustBar />

      {/* ── Logo Bar ── */}
      <LogoBar />

      {/* ── Booking Section (banded) ── */}
      <div className="gl-booking-band" data-testid="gl-booking-band">
        <div className="skye-book" id="skyeBook" data-testid="gl-booking-section">
          <div className="skye-book-chip"><i /><span>Limited spots this month</span></div>
          <h2 className="skye-book-title">
            Grab Your <span className="grad">Free Growth Planning Call.</span>
          </h2>
          <p className="skye-book-sub">
            Quick 30-min call. We'll map out exactly how AI can automate your business.
          </p>
          <ul className="skye-book-checks">
            <li><span className="ck"><svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg></span>Custom AI strategy for your business</li>
            <li><span className="ck"><svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg></span>No commitment</li>
            <li><span className="ck"><svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg></span>See exactly what we'd build for you</li>
          </ul>
        </div>

        {/* iClosed Embed — flush, no container chrome */}
        <div ref={iClosedRef} className="gl-booking-embed" data-testid="gl-booking-embed" />

        <p className="skye-book-foot">
          We only take <b>a limited number of new clients each month</b> — book now to secure your spot.
        </p>
      </div>

      {/* ── Testimonials ── */}
      <section className="gl-testimonials" data-testid="gl-testimonials">
        <h2>Real Businesses. Real Results.</h2>
        <p className="gl-testimonials__sub">
          Here's what happens when businesses plug in custom AI systems from Skye AI.
        </p>
        <div className="gl-testimonials__divider" />
        <div className="gl-testimonials__embed">
          <iframe
            ref={iframeRef}
            id="testimonialto-skye-ai"
            src="https://embed-v2.testimonial.to/w/skye-ai?id=d5ac70c8-3585-49a1-83a0-e87683acc9d8"
            frameBorder="0"
            scrolling="no"
            width="100%"
            style={{ minHeight: '200px' }}
            title="Skye AI Testimonials"
          />
        </div>
      </section>

      {/* ── CTA ── */}
      <CTAButton onClick={scrollToBooking} />

      {/* ── Logo Bar Bottom ── */}
      <LogoBar />

      {/* ── Footer ── */}
      <footer className="gl-footer" data-testid="gl-footer">
        <a href="/privacy-policy">Skye AI LLC Privacy Policy</a>
      </footer>

      {/* ── Sticky Mobile CTA ── */}
      <div
        className={`gl-sticky-cta ${showStickyCta ? 'visible' : ''}`}
        data-testid="gl-sticky-cta"
      >
        <TrailButton>
          <button className="skye-cta" onClick={scrollToBooking}>
            Book My Call <span className="arw">&rarr;</span>
          </button>
        </TrailButton>
      </div>

      {/* ── Exit Intent Popup ── */}
      {showExitIntent && (
        <div className="gl-exit-overlay" data-testid="gl-exit-overlay" onClick={() => setShowExitIntent(false)}>
          <div className="gl-exit-modal" data-testid="gl-exit-modal" onClick={(e) => e.stopPropagation()}>
            <button className="gl-exit-close" data-testid="gl-exit-close" onClick={() => setShowExitIntent(false)} aria-label="Close">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
            </button>
            <div className="gl-exit-chip">
              <i /><span>Wait — before you go</span>
            </div>
            <h2 className="gl-exit-title">
              Don't Leave Without Your <span className="grad">AI Strategy.</span>
            </h2>
            <p className="gl-exit-sub">
              A quick 30-min call and we'll map out <strong>exactly what AI systems your business needs</strong> — custom voice agents, chatbots, automations, all of it. Zero commitment.
            </p>
            <div className="gl-exit-guarantee">
              300+ businesses already running custom AI — see what's possible for yours.
            </div>
            <TrailButton>
              <button className="skye-cta" onClick={() => { setShowExitIntent(false); scrollToBooking(); }} data-testid="gl-exit-cta">
                Book My Free Call Now <span className="arw">&rarr;</span>
              </button>
            </TrailButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default GetLeads;
